import { useState, useMemo } from 'react'
import {
  Box, Typography, Chip, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper,
} from '@mui/material'
import { Analytics, Search } from '@mui/icons-material'
import ExportMenu from '../../components/common/ExportMenu'
import { formatNumber } from '../../utils/format'

const DEMO_DATA = [
  { line: 'Line 1', product: 'Chili (15oz)', cansMin: 300, ecMin: 12.5, casesHr: 180, ecsHr: 180.0 },
  { line: 'Line 2', product: 'Soup (10.75oz)', cansMin: 400, ecMin: 12.0, casesHr: 220, ecsHr: 158.4 },
  { line: 'Line 3', product: 'Beans (15oz)', cansMin: 320, ecMin: 13.3, casesHr: 190, ecsHr: 190.0 },
  { line: 'Line 4', product: 'Specialty (19oz)', cansMin: 250, ecMin: 13.2, casesHr: 160, ecsHr: 203.2 },
]

export default function CapacityRunRates() {
  const [search, setSearch] = useState('')
  const [orderBy, setOrderBy] = useState('line')
  const [order, setOrder] = useState('asc')

  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(col)
  }

  const rows = useMemo(() => {
    let filtered = DEMO_DATA.filter((r) =>
      `${r.line} ${r.product}`.toLowerCase().includes(search.toLowerCase()),
    )
    filtered.sort((a, b) => {
      const av = a[orderBy], bv = b[orderBy]
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
      return order === 'asc' ? cmp : -cmp
    })
    return filtered
  }, [search, orderBy, order])

  const columns = [
    { id: 'line', label: 'Line' },
    { id: 'product', label: 'Product' },
    { id: 'cansMin', label: 'Cans/Min', align: 'right' },
    { id: 'ecMin', label: 'EC/Min', align: 'right' },
    { id: 'casesHr', label: 'Cases/Hr', align: 'right' },
    { id: 'ecsHr', label: 'ECs/Hr', align: 'right' },
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Analytics color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Capacity Run Rates</Typography>
        <ExportMenu />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="Line Speed Standards" size="small" variant="outlined" />
        <Typography variant="overline" color="text.secondary">
          As of Mar 10, 2026 · v2 · {DEMO_DATA.length} items
        </Typography>
      </Box>

      <TextField
        size="small"
        placeholder="Search lines…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 280 }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
      />

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align || 'left'} sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.line} hover>
                <TableCell sx={{ fontWeight: 600 }}>{row.line}</TableCell>
                <TableCell>{row.product}</TableCell>
                <TableCell align="right">{formatNumber(row.cansMin)}</TableCell>
                <TableCell align="right">{row.ecMin.toFixed(1)}</TableCell>
                <TableCell align="right">{formatNumber(row.casesHr)}</TableCell>
                <TableCell align="right">{row.ecsHr.toFixed(1)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
